// ./src/utils/api/UserProfileService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";

/**
 * This is actually user preferences not user profile. We will rename it later:
 */

// =============================================================================
// GraphQL Fragments
// =============================================================================

const USER_PROFILE_FIELDS = `
  id
  accountId
  homeCurrency
  distanceIn
  volumeIn
  temperatureIn
  consumptionIn
  notifyInMileage
  notifyInDays
`;

// =============================================================================
// GraphQL Queries
// =============================================================================

const USER_PROFILE_LIST_QUERY = gql`
  query UserProfileList(
    $filter: UserProfileFilter
    $params: PaginationAndSorting
  ) {
    userProfileList(filter: $filter, params: $params) {
      code
      data {
        ${USER_PROFILE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const USER_PROFILE_GET_QUERY = gql`
  query UserProfileGet($id: ID) {
    userProfileGet(id: $id) {
      code
      data {
        ${USER_PROFILE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const USER_PROFILE_GET_MANY_QUERY = gql`
  query UserProfileGetMany($ids: [ID]) {
    userProfileGetMany(ids: $ids) {
      code
      data {
        ${USER_PROFILE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// GraphQL Mutations
// =============================================================================

const USER_PROFILE_UPDATE_MUTATION = gql`
  mutation UserProfileUpdate(
    $where: UserProfileWhereInput
    $params: UserProfileInput
  ) {
    userProfileUpdate(where: $where, params: $params) {
      code
      data {
        ${USER_PROFILE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

export interface UserProfile {
  id?: string | undefined;
  accountId?: string | undefined;
  homeCurrency?: string | undefined;
  distanceIn?: string | undefined;
  volumeIn?: string | undefined;
  temperatureIn?: string | undefined;
  consumptionIn?: string | undefined;
  notifyInMileage?: number | undefined;
  notifyInDays?: number | undefined;
}

export interface UserProfileInput {
  homeCurrency?: string | null | undefined;
  distanceIn?: string | null | undefined;
  volumeIn?: string | null | undefined;
  temperatureIn?: string | undefined;
  consumptionIn?: string | null | undefined;
  notifyInMileage?: number | null | undefined;
  notifyInDays?: number | null | undefined;
}

export interface UserProfileFilter {
  id?: string[] | null | undefined;
  accountId?: string[] | null | undefined;
}

export interface UserProfileWhereInput {
  id?: string | undefined;
}

// =============================================================================
// Service Class
// =============================================================================

/**
 * UserProfile service that handles user profile settings.
 * Contains user preferences for units (distance, volume, consumption), currency, and notification settings.
 * Profiles are created automatically with users, so only update is available (no create/remove).
 * Supports: list, get, getMany, update
 */
export class UserProfileService {
  /**
   * Get a list of user profiles with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: UserProfileFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(USER_PROFILE_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single user profile by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(USER_PROFILE_GET_QUERY, {
      id,
    });
    return result;
  }

  /**
   * Get multiple user profiles by IDs
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(USER_PROFILE_GET_MANY_QUERY, {
      ids,
    });
    return result;
  }

  /**
   * Update an existing user profile
   */
  static async update(
    where: UserProfileWhereInput,
    params: UserProfileInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      USER_PROFILE_UPDATE_MUTATION,
      {
        where,
        params,
      }
    );
    return result;
  }
}